import {
	OPTION_TYPES,
	RECENT_PRODUCT_KEY,
	RECENT_SEARCH_KEY,
} from '@/components/content/Header/constants';
import { SuggestionView } from '@/data/Content/SearchNavigation';
import { ProductType } from '@/data/types/Product';
import { RecentProduct, SuggestOption } from '@/data/types/Search';
import { uniqWith } from 'lodash';
import { CDN_BASE_URL } from 'shared/custom/constants/commonConstants';
import { Categories } from 'shared/custom/hooks/Categories';

export const capitalizeFirstLetter = (data: string): string | undefined => {
	let newData = data;
	if (!newData || newData.trim() === '') return undefined;
	newData = newData.trim().toLowerCase();
	return newData.charAt(0).toUpperCase() + newData.slice(1);
};

export const callRegex = (str: string) => {
	let newStr = str;
	const regex2 = new RegExp('>', 'ig');
	let arr: string[];
	if (newStr.match(regex2)) {
		arr = newStr.split('>');
		newStr = arr[arr.length - 1].trim();
	}
	return newStr;
};

export const highlightDiff = (option: string, query: string) => {
	if (!query) return [{ text: option, bold: false }];

	const optionLowerCase = option.toLowerCase();
	const queryLowerCase = query.toLowerCase();

	let pos = 0;

	return optionLowerCase
		.split(queryLowerCase)
		.reduce<{ text: string; bold: boolean }[]>((acc, part, index, arr) => {
			if (part) acc.push({ text: option.slice(pos, pos + part.length), bold: false });

			pos += part.length;

			if (index < arr.length - 1) {
				acc.push({ text: option.slice(pos, pos + queryLowerCase.length), bold: true });
				pos += queryLowerCase.length;
			}

			return acc;
		}, []);
};

export const getImageURL = (sku: string | undefined, url: string | undefined): string =>
	url?.startsWith('/g/')
		? `${CDN_BASE_URL}/bundles/espacios/${sku}/${sku}.jpg`
		: `${CDN_BASE_URL}/productos/${sku}/${sku}-m.jpg`;

const getIconNameFromUrl = (thumbnail?: string): string => {
	if (!thumbnail) return '';
	return thumbnail.substring(thumbnail.lastIndexOf('/') + 1);
};

export const getFormattedOptions = (
	categories: Categories[],
	suggest: SuggestionView[],
	comparator: (a: SuggestOption, b: SuggestOption) => boolean
): SuggestOption[] => {
	const MAX_OPTIONS_PER_TYPE = 5;
	const normalize = (s: string) => s.trim().toLowerCase();

	const categoryIndex = new Map<string, string>(
		categories.map((c) => [normalize(c.label), c.thumbnail ?? ''])
	);

	const buckets = suggest.reduce(
		(acc, { identifier, entry }) => {
			if (!identifier || !entry) return acc;

			const id = identifier as OPTION_TYPES;
			if (!(id in acc)) return acc;

			const options = entry.slice(0, MAX_OPTIONS_PER_TYPE).map(({ label, href }) => {
				const opt: SuggestOption = { label, href, identifier: id };

				if (id === OPTION_TYPES.Category) {
					const baseLabel = (label.split('>')?.[0] ?? label).trim();
					const key = normalize(baseLabel);
					const thumbnailUrl = categoryIndex.get(key);
					opt.thumbnail = getIconNameFromUrl(thumbnailUrl);
				}

				return opt;
			});

			acc[id].push(...options);
			return acc;
		},
		{
			[OPTION_TYPES.Keyword]: [],
			[OPTION_TYPES.Brand]: [],
			[OPTION_TYPES.Category]: [],
			[OPTION_TYPES.RecentProduct]: [],
			[OPTION_TYPES.RecentSearch]: [],
		} as Record<OPTION_TYPES, SuggestOption[]>
	);
	/* Sorting is not needed in the React version
	([OPTION_TYPES.Keyword, OPTION_TYPES.Brand, OPTION_TYPES.Category] as const).forEach((key) => {
		buckets[key].sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
	}); */

	const allOptions = [
		...buckets[OPTION_TYPES.Keyword],
		...buckets[OPTION_TYPES.Brand],
		...buckets[OPTION_TYPES.Category],
	];

	return uniqWith(allOptions, comparator);
};
const MAX_RECENT_ITEMS = 4;

export const saveSearchTerm = (newSearchTerm: string) => {
	try {
		const term = newSearchTerm.trim();
		if (!term) return;

		const stored = localStorage.getItem(RECENT_SEARCH_KEY);
		let arrayActual: string[] = stored ? JSON.parse(stored) : [];

		const normalized = term.toLowerCase();
		arrayActual = arrayActual.filter((t) => t.toLowerCase() !== normalized);
		arrayActual.unshift(term);

		if (arrayActual.length > MAX_RECENT_ITEMS) {
			arrayActual = arrayActual.slice(0, MAX_RECENT_ITEMS);
		}

		localStorage.setItem(RECENT_SEARCH_KEY, JSON.stringify(arrayActual));
	} catch {
		// localStorage corrupted or unavailable; skip silently
	}
};

export const saveRecentProduct = (product: ProductType) => {
	try {
		if (!product?.partNumber || !product?.seo?.href) return;

		const stored = localStorage.getItem(RECENT_PRODUCT_KEY);
		let arrayActual = stored ? JSON.parse(stored) : [];
		arrayActual = arrayActual.filter((p: RecentProduct) => p.sku !== product.partNumber);

		arrayActual.unshift({
			brand: product.manufacturer,
			name: product.name,
			sku: product.partNumber,
			url: product.seo.href,
		});

		if (arrayActual.length > MAX_RECENT_ITEMS) {
			arrayActual = arrayActual.slice(0, MAX_RECENT_ITEMS);
		}

		localStorage.setItem(RECENT_PRODUCT_KEY, JSON.stringify(arrayActual));
	} catch {
		// localStorage corrupted or unavailable; skip silently
	}
};

export const getRecentProducts = (): SuggestOption[] => {
	try {
		if (typeof window === 'undefined') return [];

		const storedProducts = localStorage.getItem(RECENT_PRODUCT_KEY);

		if (!storedProducts) return [];

		const productsFormatted = JSON.parse(storedProducts);
		const recentProducts = productsFormatted.map((product: RecentProduct) => ({
			label: product.name,
			href: product.url,
			identifier: OPTION_TYPES.RecentProduct,
			sku: product.sku,
			brand: product.brand,
		})) as SuggestOption[];

		return recentProducts;
	} catch {
		return [];
	}
};
export const getSearchTerms = (): SuggestOption[] => {
	try {
		if (typeof window === 'undefined') return [];

		const storedSearchTerms = localStorage.getItem(RECENT_SEARCH_KEY);

		if (!storedSearchTerms) return [];

		const searchTermsFormatted = JSON.parse(storedSearchTerms);

		const recentSearch = searchTermsFormatted.map((st: string) => ({
			label: st,
			href: '/s/' + encodeURIComponent(st),
			identifier: OPTION_TYPES.RecentSearch,
		})) as SuggestOption[];

		return recentSearch;
	} catch {
		return [];
	}
};
